import axios from 'axios';
import api from './api';
import {
    CancelarPedidoResponse,
    CaixaAbertoPedidosResponse,
} from '../types/adminCaixaMovimento';

const HTTP_FRIENDLY_MESSAGES: Record<number, string> = {
    400: 'Não foi possível concluir a solicitação. Verifique os dados e tente novamente.',
    401: 'Sua sessão expirou. Faça login novamente para continuar.',
    403: 'Você não possui permissão para executar esta ação.',
    404: 'Registro não encontrado.',
};

const GENERIC_MESSAGES_BY_STATUS: Record<string, string> = {
    'Unauthenticated.': HTTP_FRIENDLY_MESSAGES[401],
    'Nao autenticado.': HTTP_FRIENDLY_MESSAGES[401],
    'This action is unauthorized.': HTTP_FRIENDLY_MESSAGES[403],
};

export class AdminCaixaApiError extends Error {
    status: number | null;

    constructor(message: string, status: number | null) {
        super(message);
        this.name = 'AdminCaixaApiError';
        this.status = status;
    }
}

function normalizeApiError(error: unknown, fallbackMessage: string) {
    if (!axios.isAxiosError(error)) {
        return {
            status: null,
            message: fallbackMessage,
        };
    }

    const status = error.response?.status ?? null;
    const apiMessage = typeof error.response?.data?.message === 'string'
        ? error.response.data.message.trim()
        : '';

    if (apiMessage && GENERIC_MESSAGES_BY_STATUS[apiMessage]) {
        return {
            status,
            message: GENERIC_MESSAGES_BY_STATUS[apiMessage],
        };
    }

    if (apiMessage) {
        return {
            status,
            message: apiMessage,
        };
    }

    if (status && HTTP_FRIENDLY_MESSAGES[status]) {
        return {
            status,
            message: HTTP_FRIENDLY_MESSAGES[status],
        };
    }

    return {
        status,
        message: fallbackMessage,
    };
}

export async function cancelarPedido(pedidoId: number): Promise<CancelarPedidoResponse> {
    try {
        const response = await api.post<CancelarPedidoResponse>(`/auth/pedidos/${pedidoId}/cancelar`);
        return response.data;
    } catch (error) {
        const normalized = normalizeApiError(error, 'Não foi possível cancelar o pedido.');
        throw new AdminCaixaApiError(normalized.message, normalized.status);
    }
}

export async function getCaixaAbertoPedidosByUsuario(): Promise<CaixaAbertoPedidosResponse> {
    try {
        const response = await api.get<CaixaAbertoPedidosResponse>('/auth/admin/usuarios/caixa-aberto/pedidos');
        return response.data;
    } catch (error) {
        const normalized = normalizeApiError(error, 'Não foi possível carregar o caixa aberto do usuário.');

        if (normalized.status === 404) {
            return {
                message: normalized.message,
                data: null,
            };
        }

        throw new AdminCaixaApiError(normalized.message, normalized.status);
    }
}
